import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import type { CreativeAdRow } from "@shared/creative-performance";

export function creativePerformanceAdHref(brandId: string, adId: string): string {
  return `/brands/${encodeURIComponent(brandId)}/creative-performance?${new URLSearchParams({ adId })}`;
}

/** Resolve a saved result's source without changing the dashboard's filters or metrics. */
export function useCreativePerformanceAdLink({
  brandId, ads, ready, feedStart, onSelect,
}: {
  brandId: string;
  ads: CreativeAdRow[];
  ready: boolean;
  feedStart: string;
  onSelect: (ad: CreativeAdRow | null) => void;
}) {
  const search = useSearch();
  const [location, setLocation] = useLocation();
  const adId = new URLSearchParams(search).get("adId")?.trim() || "";
  const current = ads.find((ad) => ad.adId === adId);
  const requestKey = adId ? `${brandId}:${adId}` : "";
  const opened = useRef("");
  const history = useQuery<CreativeAdRow[]>({
    queryKey: [`/api/brands/${brandId}/creative-performance/ads?since=${feedStart}&until=${new Date().toISOString().slice(0, 10)}&tier=All`],
    enabled: Boolean(brandId && adId && ready && !current),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const historical = history.data?.find((ad) => ad.adId === adId);
  const resolved = ready ? current ?? historical : undefined;

  useEffect(() => {
    if (!requestKey) {
      opened.current = "";
      return;
    }
    if (opened.current === requestKey) return;
    // Never leave the previous source visible while resolving another brand/ad.
    onSelect(resolved ?? null);
    if (resolved) opened.current = requestKey;
  }, [requestKey, resolved, onSelect]);

  const clear = () => {
    if (!adId) return;
    const params = new URLSearchParams(search);
    params.delete("adId");
    setLocation(`${location.split("?")[0]}${params.size ? `?${params}` : ""}`, { replace: true });
  };
  return {
    clear,
    retry: () => history.refetch(),
    status: !adId || current ? null
      : !ready || history.isLoading ? "loading"
      : history.isError ? "error"
      : historical ? "historical"
      : history.isSuccess ? "missing" : null,
  };
}